// Operations.jsx — Bank Own Operations (branches, offices, data centres, supply chain)
import React, { useEffect, useState } from "react";
import {
  Box, Paper, Button, Table, TableHead, TableRow, TableCell, TableBody,
  TextField, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, IconButton, Tooltip, CircularProgress, Alert,
} from "@mui/material";
import {
  Plus, Search, ArrowRight, Trash2, Filter, RefreshCw, Building2, Server, Truck,
  Landmark, Shield, MapPin, PlayCircle,
} from "lucide-react";
import { toast } from "react-toastify";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import DashboardLayout from "../layouts/DashboardLayout";
import KpiCard from "../components/KpiCard";
import RiskPill from "../components/RiskPill";
import ScenarioToggle from "../components/ScenarioToggle";
import { facilitiesApi } from "../api/extra";
import { collateralsApi, formatApiError } from "../api/api";
import {
  fmtINR, fmtScore, fmtPct, fmtNum, HAZARD_LABELS, riskColor,
} from "../utils/format";

const FACILITY_TYPES = [
  { value: "head_office", label: "Head Office", icon: Landmark },
  { value: "regional_office", label: "Regional Office", icon: Building2 },
  { value: "branch", label: "Branch", icon: Building2 },
  { value: "data_center", label: "Data Centre", icon: Server },
  { value: "currency_chest", label: "Currency Chest", icon: Shield },
  { value: "atm", label: "ATM", icon: Building2 },
  { value: "supply_chain", label: "Supply Chain Node", icon: Truck },
];

const BCP_TIERS = ["Standard", "Critical", "Tier-1"];
const CONSTRUCTION = ["rcc", "steel", "brick", "semi-pucca", "kuccha", "prefab"];

const facilityIcon = (t) => (FACILITY_TYPES.find((x) => x.value === t) || {}).icon || Building2;
const facilityLabel = (t) => (FACILITY_TYPES.find((x) => x.value === t) || {}).label || t;

export default function Operations() {
  const [scenario, setScenario] = useState("current");
  const [summary, setSummary] = useState(null);
  const [list, setList] = useState([]);
  const [filterType, setFilterType] = useState("");
  const [filterOwnership, setFilterOwnership] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [rescoring, setRescoring] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(defaultForm());
  const [saving, setSaving] = useState(false);
  const [geocoding, setGeocoding] = useState(false);

  function defaultForm() {
    return {
      name: "", facility_type: "branch", ownership: "owned",
      pin_code: "", state: "", city: "", address: "",
      latitude: "", longitude: "",
      headcount: "", annual_opex_inr: "", replacement_value_inr: "",
      business_continuity_tier: "Standard",
      asset_age_years: 0, construction_type: "rcc",
      elevation_m: "", coastal_distance_km: "",
      insurance_cover_inr: 0, has_dr_site: false, dr_site_distance_km: "",
    };
  }

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        facilitiesApi.summary(scenario),
        facilitiesApi.list(filterType ? { facility_type: filterType, ownership: filterOwnership || undefined } : (filterOwnership ? { ownership: filterOwnership } : {})),
      ]);
      setSummary(a.data);
      setList(b.data.facilities || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scenario, filterType, filterOwnership]);

  const rescore = async () => {
    setRescoring(true);
    try {
      await facilitiesApi.assessAll(scenario);
      toast.success("All facilities re-scored");
      load();
    } catch (e) { toast.error("Re-score failed"); }
    finally { setRescoring(false); }
  };

  const resolvePin = async () => {
    const p = form.pin_code?.trim();
    if (!/^\d{6}$/.test(p)) { toast.warn("Enter 6-digit PIN"); return; }
    setGeocoding(true);
    try {
      const { data } = await collateralsApi.geocode(p);
      setForm((f) => ({
        ...f, latitude: data.latitude.toFixed(6), longitude: data.longitude.toFixed(6),
        state: f.state || data.state || "", city: f.city || data.city || "",
      }));
      toast.success(`PIN ${p} resolved`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setGeocoding(false); }
  };

  const save = async () => {
    if (!form.name || !form.latitude || !form.longitude) { toast.warn("Name + lat/lon required"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      ["headcount", "annual_opex_inr", "replacement_value_inr", "asset_age_years",
        "elevation_m", "coastal_distance_km", "insurance_cover_inr", "dr_site_distance_km",
        "latitude", "longitude"].forEach((k) => {
          payload[k] = payload[k] === "" || payload[k] == null ? null : Number(payload[k]);
        });
      const { data } = await facilitiesApi.create(payload);
      try { await facilitiesApi.assess(data.facility.facility_id, scenario); } catch {}
      toast.success("Facility added and assessed");
      setOpen(false); setForm(defaultForm()); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete "${f.name}"?`)) return;
    try { await facilitiesApi.remove(f.facility_id); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const results = (summary?.results || []);
  const scoreMap = {};
  results.forEach((r) => { scoreMap[r.facility_id] = r; });
  const filtered = list.filter((f) =>
    !search || (f.name + " " + (f.city || "") + " " + (f.state || "")).toLowerCase().includes(search.toLowerCase())
  );

  const s = summary?.summary || {};
  const byType = summary?.by_facility_type || [];

  return (
    <DashboardLayout
      title="Bank Own Operations"
      subtitle="BRANCHES · OFFICES · DATA CENTRES · SUPPLY CHAIN"
      headerExtra={
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <ScenarioToggle value={scenario} onChange={setScenario} />
          <Button size="small" variant="contained" onClick={rescore} disabled={rescoring}
            startIcon={rescoring ? <CircularProgress size={12} sx={{ color: "#FFF" }} /> : <RefreshCw size={13} />}
            data-testid="rescore-facilities-btn"
            sx={{ background: "#2E5A35", "&:hover": { background: "#1f4023" } }}>
            {rescoring ? "Re-scoring" : "Re-score all"}
          </Button>
          <Button size="small" variant="outlined" onClick={() => setOpen(true)}
            startIcon={<Plus size={13} />} data-testid="add-facility-btn">
            Add facility
          </Button>
        </Box>
      }>

      <Alert severity="info" sx={{ mb: 2 }}>
        Physical-climate risk on the bank's own operations — branches, regional offices, data centres,
        currency chests and critical supply-chain nodes (whether owned or leased). Score factors in the
        facility's Business-Continuity tier so a Tier-1 data centre carries an extra severity multiplier.
      </Alert>

      {/* KPI cards */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 1.6, mb: 2 }}>
        <KpiCard label="Facilities" value={fmtNum(summary?.n_facilities)}
          sub={`${s.collateral_count || 0} scored · ${scenario}`} accent="#1E2A38" testid="kpi-fac-total" />
        <KpiCard label="Avg Operational Score" value={fmtScore(s.avg_score)}
          sub={`max ${fmtScore(s.max_score)}`} accent="#8F5E14" testid="kpi-fac-avg" />
        <KpiCard label="High-Risk Share" value={fmtPct(s.high_risk_pct)}
          sub={`${(s.level_counts?.High || 0) + (s.level_counts?.Critical || 0)} facilities`}
          accent="#A93226" testid="kpi-fac-high" />
        <KpiCard label="Replacement Value at Risk" value={fmtINR(s.total_financial_exposure_inr)}
          sub="loss exposure at this scenario" accent="#2E5A35" testid="kpi-fac-exposure" />
      </Box>

      {/* Map + breakdown */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1.4fr 1fr" }, gap: 2, mb: 2 }}>
        <Paper sx={{ p: 0, overflow: "hidden", height: 420 }} data-testid="facilities-map">
          <MapContainer center={[22.5, 80]} zoom={5} style={{ height: "100%", width: "100%" }}
            scrollWheelZoom={true}>
            <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
              attribution='&copy; OpenStreetMap, &copy; CARTO' />
            {results.filter((r) => r.latitude && r.longitude).map((r) => (
              <CircleMarker key={r.facility_id}
                center={[r.latitude, r.longitude]}
                radius={9 + (Math.log10((r.replacement_value_inr || 1) / 1e7 + 1) * 3)}
                pathOptions={{ color: riskColor(r.risk_level), fillColor: riskColor(r.risk_level), fillOpacity: 0.85, weight: 2 }}>
                <Popup>
                  <div style={{ fontFamily: "IBM Plex Sans, sans-serif", fontSize: 12.5, minWidth: 220 }}>
                    <div style={{ fontWeight: 700 }}>{r.facility_name}</div>
                    <div style={{ color: "#4B5563" }}>{facilityLabel(r.facility_type)} · {r.ownership}</div>
                    <div style={{ marginTop: 4, fontFamily: "IBM Plex Mono, monospace", fontSize: 11.5 }}>
                      Score: {fmtScore(r.overall_score)} · {r.risk_level}<br />
                      Top: {HAZARD_LABELS[r.top_hazard] || r.top_hazard || "—"}<br />
                      Value: {fmtINR(r.replacement_value_inr)}
                    </div>
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </Paper>

        <Paper sx={{ p: 2.2 }} data-testid="facilities-by-type">
          <Box className="cb-display" sx={{ fontSize: 16, fontWeight: 600, color: "#111827", mb: 1.5 }}>
            By Facility Type
          </Box>
          {byType.length === 0 && <Box sx={{ fontSize: 12, color: "#94A3B8" }}>Re-score to populate.</Box>}
          {byType.map((t) => {
            const Icon = facilityIcon(t.facility_type);
            return (
              <Box key={t.facility_type} sx={{ py: 0.8, borderTop: "1px solid #F0EFEA" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Icon size={14} color="#1E2A38" />
                    <Box sx={{ fontSize: 13, fontWeight: 600 }}>{facilityLabel(t.facility_type)}</Box>
                    <Chip size="small" label={`${t.count}`} sx={{ fontSize: 9.5, height: 18 }} />
                  </Box>
                  <Box className="cb-mono" sx={{ fontSize: 12.5, fontWeight: 600 }}>
                    {fmtScore(t.avg_score)}
                  </Box>
                </Box>
                <Box className="cb-mono" sx={{ fontSize: 11, color: "#6B7280", mt: 0.4 }}>
                  {t.high} high-risk · {fmtINR(t.value_sum)} replacement
                </Box>
                <Box sx={{ background: "#F0EFEA", borderRadius: 1, height: 4, mt: 0.5, overflow: "hidden" }}>
                  <Box sx={{ width: `${Math.min(100, t.avg_score)}%`, height: "100%", background: riskColor(t.avg_score > 65 ? "High" : t.avg_score > 45 ? "Medium" : "Low") }} />
                </Box>
              </Box>
            );
          })}
        </Paper>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 1.4, mb: 2, display: "flex", gap: 1.2, alignItems: "center", flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, color: "#6B7280", fontSize: 11.5, fontFamily: "IBM Plex Mono, monospace", letterSpacing: "0.1em", textTransform: "uppercase" }}>
          <Filter size={13} /> Filters
        </Box>
        <TextField size="small" placeholder="Search name / city / state…" value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <Search size={14} style={{ marginRight: 6, color: "#94A3B8" }} /> }}
          inputProps={{ "data-testid": "fac-search" }} sx={{ minWidth: 240 }} />
        <TextField size="small" select label="Type" value={filterType}
          onChange={(e) => setFilterType(e.target.value)} sx={{ minWidth: 170 }}
          SelectProps={{ inputProps: { "data-testid": "fac-filter-type" } }}>
          <MenuItem value="">All types</MenuItem>
          {FACILITY_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
        </TextField>
        <TextField size="small" select label="Ownership" value={filterOwnership}
          onChange={(e) => setFilterOwnership(e.target.value)} sx={{ minWidth: 130 }}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="owned">Owned</MenuItem>
          <MenuItem value="leased">Leased</MenuItem>
        </TextField>
        <Box sx={{ flex: 1 }} />
        <Chip size="small" label={`${filtered.length} facilities`} data-testid="fac-count-chip" />
      </Paper>

      {/* Table */}
      <Paper sx={{ overflow: "hidden" }} data-testid="facilities-table">
        {loading ? <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress size={24} /></Box> : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Facility</TableCell>
                <TableCell>Type · Ownership</TableCell>
                <TableCell>BCP Tier</TableCell>
                <TableCell>City · State</TableCell>
                <TableCell align="right">Replacement</TableCell>
                <TableCell>Top hazard</TableCell>
                <TableCell align="right">Score</TableCell>
                <TableCell align="right">Risk</TableCell>
                <TableCell></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={9} sx={{ py: 4, color: "#94A3B8", textAlign: "center" }}>
                  No facilities match filter.
                </TableCell></TableRow>
              )}
              {filtered.map((f) => {
                const r = scoreMap[f.facility_id];
                const Icon = facilityIcon(f.facility_type);
                return (
                  <TableRow key={f.facility_id} hover data-testid={`fac-row-${f.facility_id}`}>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                        <Icon size={14} color="#1E2A38" />
                        <Box>
                          <Box sx={{ fontWeight: 600, color: "#111827" }}>{f.name}</Box>
                          <Box className="cb-mono" sx={{ fontSize: 11, color: "#6B7280" }}>PIN {f.pin_code || "—"}</Box>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ fontSize: 12, fontWeight: 600 }}>{facilityLabel(f.facility_type)}</Box>
                      <Chip size="small" label={f.ownership}
                        sx={{
                          fontSize: 9.5, textTransform: "uppercase",
                          background: f.ownership === "owned" ? "#E8F0E9" : "#F0EFEA",
                          color: f.ownership === "owned" ? "#2E5A35" : "#4B5563",
                        }} />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={f.business_continuity_tier}
                        sx={{
                          fontSize: 9.5,
                          background: f.business_continuity_tier === "Tier-1" ? "#FCE8E6"
                            : f.business_continuity_tier === "Critical" ? "#FFF4E5" : "#F0EFEA",
                          color: f.business_continuity_tier === "Tier-1" ? "#A93226"
                            : f.business_continuity_tier === "Critical" ? "#8F5E14" : "#4B5563",
                          fontWeight: 600,
                        }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{f.city} · {f.state}</TableCell>
                    <TableCell align="right" className="cb-mono">{fmtINR(f.replacement_value_inr)}</TableCell>
                    <TableCell>
                      {r?.top_hazard ? <span className="hazard-chip">{HAZARD_LABELS[r.top_hazard] || r.top_hazard}</span> : "—"}
                    </TableCell>
                    <TableCell align="right" className="cb-mono" sx={{ fontWeight: 600 }}>{fmtScore(r?.overall_score)}</TableCell>
                    <TableCell align="right">{r ? <RiskPill level={r.risk_level} /> : <Chip size="small" label="UNSCORED" sx={{ fontSize: 9.5 }} />}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Re-score">
                        <IconButton size="small" onClick={async () => {
                          try { await facilitiesApi.assess(f.facility_id, scenario); toast.success("Re-scored"); load(); }
                          catch (e) { toast.error(formatApiError(e)); }
                        }} data-testid={`fac-rescore-${f.facility_id}`}>
                          <RefreshCw size={13} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => remove(f)} data-testid={`fac-del-${f.facility_id}`}>
                          <Trash2 size={13} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Add dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontFamily: "Playfair Display, serif", fontWeight: 600 }}>Add bank facility</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.6 }}>
            <TextField size="small" required label="Facility name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} inputProps={{ "data-testid": "ff-name" }} />
            <TextField size="small" select label="Type" value={form.facility_type}
              onChange={(e) => setForm({ ...form, facility_type: e.target.value })}
              SelectProps={{ inputProps: { "data-testid": "ff-type" } }}>
              {FACILITY_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
            <TextField size="small" select label="Ownership" value={form.ownership}
              onChange={(e) => setForm({ ...form, ownership: e.target.value })}>
              <MenuItem value="owned">Owned</MenuItem>
              <MenuItem value="leased">Leased</MenuItem>
            </TextField>
            <TextField size="small" select label="Business Continuity Tier" value={form.business_continuity_tier}
              onChange={(e) => setForm({ ...form, business_continuity_tier: e.target.value })}>
              {BCP_TIERS.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField size="small" required label="PIN code" value={form.pin_code}
                onChange={(e) => setForm({ ...form, pin_code: e.target.value })}
                inputProps={{ "data-testid": "ff-pin", maxLength: 6 }} sx={{ flex: 1 }} />
              <Button onClick={resolvePin} variant="outlined" disabled={geocoding}
                data-testid="ff-resolve-pin" sx={{ minWidth: 100 }}>
                {geocoding ? <CircularProgress size={12} /> : "Resolve"}
              </Button>
            </Box>
            <TextField size="small" label="Address" value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })} />
            <TextField size="small" label="City" value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })} />
            <TextField size="small" label="State" value={form.state}
              onChange={(e) => setForm({ ...form, state: e.target.value })} />
            <TextField size="small" required label="Latitude" type="number" value={form.latitude}
              onChange={(e) => setForm({ ...form, latitude: e.target.value })}
              inputProps={{ "data-testid": "ff-lat" }} />
            <TextField size="small" required label="Longitude" type="number" value={form.longitude}
              onChange={(e) => setForm({ ...form, longitude: e.target.value })}
              inputProps={{ "data-testid": "ff-lon" }} />
            <TextField size="small" label="Headcount" type="number" value={form.headcount}
              onChange={(e) => setForm({ ...form, headcount: e.target.value })} />
            <TextField size="small" label="Annual opex (INR)" type="number" value={form.annual_opex_inr}
              onChange={(e) => setForm({ ...form, annual_opex_inr: e.target.value })} />
            <TextField size="small" required label="Replacement value (INR)" type="number" value={form.replacement_value_inr}
              onChange={(e) => setForm({ ...form, replacement_value_inr: e.target.value })}
              inputProps={{ "data-testid": "ff-value" }} />
            <TextField size="small" label="Insurance cover (INR)" type="number" value={form.insurance_cover_inr}
              onChange={(e) => setForm({ ...form, insurance_cover_inr: e.target.value })} />
            <TextField size="small" select label="Construction" value={form.construction_type}
              onChange={(e) => setForm({ ...form, construction_type: e.target.value })}>
              {CONSTRUCTION.map((c) => <MenuItem key={c} value={c}>{c.toUpperCase()}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Asset age (yrs)" type="number" value={form.asset_age_years}
              onChange={(e) => setForm({ ...form, asset_age_years: e.target.value })} />
            <TextField size="small" label="Elevation (m)" type="number" value={form.elevation_m}
              onChange={(e) => setForm({ ...form, elevation_m: e.target.value })} />
            <TextField size="small" label="Coastal distance (km)" type="number" value={form.coastal_distance_km}
              onChange={(e) => setForm({ ...form, coastal_distance_km: e.target.value })} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving}
            data-testid="ff-save" startIcon={<PlayCircle size={13} />}
            sx={{ background: "#1E2A38", "&:hover": { background: "#0F1721" } }}>
            {saving ? "Saving…" : "Add & assess"}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
